import * as THREE from "../../../libs/three.js/build/three.module.js";
import {
	applySparkStyleShaderPatch,
	SPARK_STYLE_PROGRAM_CACHE_KEY
} from "./sparkStyleShaderPatch.js";

const SPARK_STYLE_MODES = {
	DEFAULT: "default",
	POINT: "point",
	BORDER: "border",
	RING: "ring"
};

export class SparkStyleController {
	constructor(options = {}) {
		this.sparkRenderer = options.sparkRenderer || null;
		this.debug = options.debug === true;
		this.maxPoints = Number.isFinite(options.maxPoints) ? options.maxPoints : 200000;
		this.pointSize = Number.isFinite(options.pointSize) ? options.pointSize : 2.5;
		this.pointColor = new THREE.Color(options.pointColor ?? 0xffdd44);
		this.outlineSize = Number.isFinite(options.outlineSize) ? options.outlineSize : 6.0;
		this.outlineThickness = Number.isFinite(options.outlineThickness) ? options.outlineThickness : 0.25;
		this.outlineColor = new THREE.Color(options.outlineColor ?? 0x111111);
		this.ringSize = Number.isFinite(options.ringSize) ? options.ringSize : 0.04;
		this.pointModeSize = Number.isFinite(options.pointModeSize) ? options.pointModeSize : 3.0;
		this.mode = SPARK_STYLE_MODES.DEFAULT;
		this.meshEntries = new Map();
		this.sparkDefaults = null;
		this.sparkShaderUniforms = null;
		this.sparkShaderPatched = false;
		if (this.sparkRenderer) {
			this.sparkDefaults = {
				enable2DGS: this.sparkRenderer.enable2DGS,
				falloff: this.sparkRenderer.falloff,
				minPixelRadius: this.sparkRenderer.minPixelRadius
			};
			this._installSparkStyleShaderPatch();
			this._log("init defaults", this.sparkDefaults);
		}
	}

	async attachToMesh(mesh, options = {}) {
		if (!mesh || this.meshEntries.has(mesh)) {
			return false;
		}

		if (mesh.initialized && typeof mesh.initialized.then === "function") {
			try {
				await mesh.initialized;
			} catch (error) {
				console.warn("SparkStyleController: mesh initialization failed", error);
			}
		}

		const originalOpacity = (typeof mesh.opacity === "number") ? mesh.opacity : null;
		this.meshEntries.set(mesh, {
			mesh,
			originalOpacity
		});

		this._applyModeToEntry(this.meshEntries.get(mesh), this.mode);
		this._markSparkDirty();
		this._log("attach ok", {
			mesh: mesh.name || mesh.constructor?.name || "unknown",
			samples: Number.isFinite(mesh?.numSplats) ? mesh.numSplats : null,
			source: "shader-mode"
		});

		return true;
	}

	detachFromMesh(mesh) {
		const entry = this.meshEntries.get(mesh);
		if (!entry) {
			return false;
		}

		if (entry.originalOpacity != null && typeof entry.mesh.opacity === "number") {
			entry.mesh.opacity = entry.originalOpacity;
		}
		entry.mesh.visible = true;

		this.meshEntries.delete(mesh);
		this._markSparkDirty();
		this._log("detach ok", {
			mesh: mesh.name || mesh.constructor?.name || "unknown"
		});
		return true;
	}

	setMode(mode) {
		const normalized = String(mode || "").toLowerCase();
		const nextMode = Object.values(SPARK_STYLE_MODES).includes(normalized)
			? normalized
			: SPARK_STYLE_MODES.DEFAULT;

		this.mode = nextMode;
		for (const entry of this.meshEntries.values()) {
			this._applyModeToEntry(entry, nextMode);
		}
		this._applySparkRendererMode(nextMode);
		this._markSparkDirty();
		this._log("mode", {
			mode: nextMode,
			helpers: this.meshEntries.size
		});
	}

	_applyModeToEntry(entry, mode) {
		// Legacy overlay styles removed: rendering modes are now shader-driven only.
		void entry;
		void mode;
	}

	_createHelperForMesh(mesh, options = {}) {
		const limit = options.maxPoints ?? this.maxPoints;
		let centers = this._extractSplatCenters(mesh, limit);
		let source = "splats";
		if (!centers || centers.length === 0) {
			centers = this._extractFallbackCentersFromBoundingBox(mesh, limit);
			source = "bbox-fallback";
		}
		if (!centers || centers.length === 0) {
			centers = [
				0, 0, 0,
				1, 0, 0,
				-1, 0, 0,
				0, 1, 0,
				0, -1, 0,
				0, 0, 1,
				0, 0, -1
			];
			source = "origin-fallback";
		}

		centers = this._sanitizeCenters(centers);
		if (centers.length === 0) {
			centers = [
				0, 0, 0,
				1, 0, 0,
				-1, 0, 0
			];
			source = "origin-fallback";
		}

		const positionArray = new Float32Array(centers);
		const geometry = new THREE.BufferGeometry();
		geometry.setAttribute("position", new THREE.BufferAttribute(positionArray, 3));

		const pointMaterial = new THREE.PointsMaterial({
			size: options.pointSize ?? this.pointSize,
			color: options.pointColor ?? this.pointColor,
			sizeAttenuation: false,
			transparent: true,
			opacity: 1.0,
			depthTest: false,
			depthWrite: false
		});

		const outlineMaterial = new THREE.ShaderMaterial({
			uniforms: {
				uColor: {value: new THREE.Color(options.outlineColor ?? this.outlineColor)},
				uSize: {value: options.outlineSize ?? this.outlineSize},
				uThickness: {value: options.outlineThickness ?? this.outlineThickness}
			},
			vertexShader: `
				uniform float uSize;
				void main(){
					vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
					gl_Position = projectionMatrix * mvPosition;
					gl_PointSize = uSize;
				}
			`,
			fragmentShader: `
				uniform vec3 uColor;
				uniform float uThickness;
				void main(){
					vec2 c = gl_PointCoord * 2.0 - 1.0;
					float r = length(c);
					float inner = clamp(1.0 - uThickness, 0.0, 1.0);
					if(r > 1.0 || r < inner){
						discard;
					}
					gl_FragColor = vec4(uColor, 1.0);
				}
			`,
			transparent: true,
			depthTest: false,
			depthWrite: false
		});

		const points = new THREE.Points(geometry, pointMaterial);
		const outline = new THREE.Points(geometry, outlineMaterial);
		points.frustumCulled = false;
		outline.frustumCulled = false;
		points.renderOrder = 10000;
		outline.renderOrder = 10001;
		points.visible = false;
		outline.visible = false;

		const root = new THREE.Object3D();
		root.name = "SparkStyleHelper";
		root.add(points);
		root.add(outline);

		return {
			root,
			point: points,
			outline,
			sampleCount: positionArray.length / 3,
			source: source,
			dispose: () => {
				geometry.dispose();
				pointMaterial.dispose();
				outlineMaterial.dispose();
			}
		};
	}

	_extractSplatCenters(mesh, maxPoints) {
		if (mesh && typeof mesh.forEachSplat === "function") {
			const numSplats = Number.isFinite(mesh.numSplats) ? mesh.numSplats : 0;
			const stride = (numSplats > 0 && maxPoints > 0) ? Math.max(1, Math.ceil(numSplats / maxPoints)) : 1;
			const centers = [];
			let fallbackIndex = 0;

			try {
				mesh.forEachSplat((...args) => {
					let index = fallbackIndex++;
					let center = null;

					if (typeof args[0] === "number") {
						index = args[0];
						center = this._normalizeCenter(args[1]);
					} else {
						center = this._findCenterInArgs(args);
					}
					if (!center || (index % stride !== 0)) {
						return;
					}
					this._pushFiniteCenter(centers, center.x, center.y, center.z);
				});
			} catch (error) {
				this._log("mesh.forEachSplat failed", {
					error: error && error.message ? error.message : String(error)
				});
			}

			if (centers.length > 0) {
				this._log("centers from mesh.forEachSplat", {
					samples: centers.length / 3
				});
				return centers;
			}
		}

		const packedSplats = mesh && mesh.packedSplats;
		if (!packedSplats) {
			this._log("no packedSplats on mesh", {
				mesh: mesh && (mesh.name || mesh.constructor?.name || "unknown")
			});
			return [];
		}
		this._inspectPackedSplats(packedSplats);

		const numSplats = Number.isFinite(packedSplats.numSplats) ? packedSplats.numSplats : 0;
		const stride = (numSplats > 0 && maxPoints > 0) ? Math.max(1, Math.ceil(numSplats / maxPoints)) : 1;
		const centers = [];

		if (typeof packedSplats.forEachSplat === "function") {
			let fallbackIndex = 0;
			let argSampleCount = 0;
			packedSplats.forEachSplat((...args) => {
				if (argSampleCount < 3) {
					this._log("forEachSplat args sample", {
						index: argSampleCount,
						types: args.map(a => this._valueType(a)),
						preview: args.map(a => this._previewValue(a))
					});
					argSampleCount++;
				}

				let index = null;
				let center = null;

				if (typeof args[0] === "number") {
					index = args[0];
					center = this._findCenterInArgs(args.slice(1));
				} else {
					index = fallbackIndex;
					center = this._findCenterInArgs(args);
					fallbackIndex++;
				}

				if (!center) {
					return;
				}
				if (index % stride !== 0) {
					return;
				}

				this._pushFiniteCenter(centers, center.x, center.y, center.z);
			});

			if (centers.length > 0) {
				return centers;
			}
		}

		if (typeof packedSplats.getSplat === "function" && numSplats > 0) {
			const sampleIndices = [0, 1, 2].filter(i => i < numSplats);
			for (const sampleIndex of sampleIndices) {
				try {
					const sample = packedSplats.getSplat(sampleIndex);
					this._log("getSplat sample", {
						index: sampleIndex,
						type: this._valueType(sample),
						preview: this._previewValue(sample)
					});
				} catch (error) {
					this._log("getSplat sample error", {
						index: sampleIndex,
						error: error && error.message ? error.message : String(error)
					});
				}
			}

			for (let i = 0; i < numSplats; i += stride) {
				const splat = packedSplats.getSplat(i);
				const center = this._normalizeCenter(splat) || this._normalizeCenter(splat && splat.center);
				if (!center) {
					continue;
				}
				this._pushFiniteCenter(centers, center.x, center.y, center.z);
			}
		}

		// Fallback: some Spark builds expose center arrays directly.
		if (centers.length === 0 && numSplats > 0) {
			const keys = Object.keys(packedSplats || {});
			const centerKey = keys.find(key => /(center|position|xyz)/i.test(key));
			if (centerKey) {
				const arr = packedSplats[centerKey];
				if (arr && typeof arr.length === "number" && arr.length >= numSplats * 3) {
					const componentStride = Math.floor(arr.length / numSplats);
					if (componentStride >= 3) {
						for (let i = 0; i < numSplats; i += stride) {
							const base = i * componentStride;
							centers.push(arr[base], arr[base + 1], arr[base + 2]);
						}
					}
				}
			}
		}

		return centers;
	}

	_inspectPackedSplats(packedSplats) {
		const keys = Object.keys(packedSplats || {});
		const keyPreview = keys.slice(0, 40);
		this._log("packedSplats keys", {
			total: keys.length,
			keys: keyPreview
		});
		this._log("packedSplats meta", {
			numSplats: packedSplats.numSplats,
			hasForEachSplat: typeof packedSplats.forEachSplat === "function",
			hasGetSplat: typeof packedSplats.getSplat === "function"
		});
	}

	_findCenterInArgs(args) {
		for (const arg of args) {
			const center = this._normalizeCenter(arg);
			if (center) {
				return center;
			}
			if (arg && typeof arg === "object") {
				const nested = this._normalizeCenter(arg.center) || this._normalizeCenter(arg.position);
				if (nested) {
					return nested;
				}
			}
		}
		return null;
	}

	_normalizeCenter(value) {
		if (!value) {
			return null;
		}
		if (this._isFiniteXYZ(value.x, value.y, value.z)) {
			return {x: value.x, y: value.y, z: value.z};
		}
		if (Array.isArray(value) && value.length >= 3 && this._isFiniteXYZ(value[0], value[1], value[2])) {
			return {x: value[0], y: value[1], z: value[2]};
		}
		if (value && typeof value === "object" && this._isFiniteXYZ(value[0], value[1], value[2])) {
			return {x: value[0], y: value[1], z: value[2]};
		}
		return null;
	}

	_valueType(value) {
		if (value === null) {
			return "null";
		}
		if (Array.isArray(value)) {
			return `array(${value.length})`;
		}
		const t = typeof value;
		if (t !== "object") {
			return t;
		}
		return value.constructor && value.constructor.name ? value.constructor.name : "object";
	}

	_previewValue(value) {
		if (value == null) {
			return value;
		}
		if (Array.isArray(value)) {
			return value.slice(0, 6);
		}
		if (typeof value !== "object") {
			return value;
		}

		const out = {};
		const keys = Object.keys(value).slice(0, 12);
		for (const key of keys) {
			const v = value[key];
			if (v == null || typeof v === "number" || typeof v === "string" || typeof v === "boolean") {
				out[key] = v;
			} else if (Array.isArray(v)) {
				out[key] = `[array:${v.length}]`;
			} else if (typeof v === "object") {
				out[key] = v.constructor && v.constructor.name ? `[${v.constructor.name}]` : "[object]";
			} else {
				out[key] = `[${typeof v}]`;
			}
		}
		return out;
	}

	_extractFallbackCentersFromBoundingBox(mesh, maxPoints) {
		if (!mesh || typeof mesh.getBoundingBox !== "function") {
			return [];
		}

		let box = null;
		try {
			box = mesh.getBoundingBox(true);
		} catch (error) {
			this._log("bbox fallback failed", {reason: error && error.message ? error.message : "unknown"});
			return [];
		}

		if (!box || !box.isBox3) {
			return [];
		}

		const min = box.min;
		const max = box.max;
		const size = box.getSize(new THREE.Vector3());
		if (![size.x, size.y, size.z].every(Number.isFinite)) {
			return [];
		}

		const centers = [];
		const targetCount = Math.max(64, Math.min(maxPoints || 2000, 6000));
		const n = Math.ceil(Math.cbrt(targetCount));
		const sx = n > 1 ? (max.x - min.x) / (n - 1) : 0;
		const sy = n > 1 ? (max.y - min.y) / (n - 1) : 0;
		const sz = n > 1 ? (max.z - min.z) / (n - 1) : 0;
		const tmpWorld = new THREE.Vector3();
		const tmpLocal = new THREE.Vector3();
		mesh.updateMatrixWorld(true);

		for (let ix = 0; ix < n; ix++) {
			for (let iy = 0; iy < n; iy++) {
				for (let iz = 0; iz < n; iz++) {
					tmpWorld.set(
						min.x + ix * sx,
						min.y + iy * sy,
						min.z + iz * sz
					);
					// getBoundingBox() is often world-space for splat meshes.
					// Convert to local because helper is attached under mesh.
					tmpLocal.copy(tmpWorld);
					mesh.worldToLocal(tmpLocal);
					// Add both candidates:
					// 1) local-transformed (for world-space bbox)
					this._pushFiniteCenter(centers, tmpLocal.x, tmpLocal.y, tmpLocal.z);
					// 2) raw sample (for already-local bbox)
					this._pushFiniteCenter(centers, tmpWorld.x, tmpWorld.y, tmpWorld.z);
					if (centers.length / 3 >= targetCount * 2) {
						return centers;
					}
				}
			}
		}

		return centers;
	}

	_markSparkDirty() {
		if (this.sparkRenderer && "needsUpdate" in this.sparkRenderer) {
			this.sparkRenderer.needsUpdate = true;
		}
	}

	_pushFiniteCenter(buffer, x, y, z) {
		if (this._isFiniteXYZ(x, y, z)) {
			buffer.push(x, y, z);
		}
	}

	_isFiniteXYZ(x, y, z) {
		return Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z);
	}

	_sanitizeCenters(centers) {
		const sanitized = [];
		let dropped = 0;
		for (let i = 0; i + 2 < centers.length; i += 3) {
			const x = centers[i];
			const y = centers[i + 1];
			const z = centers[i + 2];
			if ([x, y, z].every(Number.isFinite)) {
				sanitized.push(x, y, z);
			} else {
				dropped++;
			}
		}

		if (dropped > 0) {
			this._log("dropped invalid centers", {dropped});
		}

		return sanitized;
	}

	_applySparkRendererMode(mode) {
		if (!this.sparkRenderer) {
			this._log("renderer fallback skipped", {reason: "no renderer"});
			return;
		}

		const defaults = this.sparkDefaults || {};
		this._setSparkStyleUniforms(mode);
		// Supersplat-like approach: mode appearance comes from shader uniforms.
		// Keep SparkRenderer base params stable across default / point / ring.
		if ("enable2DGS" in this.sparkRenderer && defaults.enable2DGS !== undefined) {
			this.sparkRenderer.enable2DGS = defaults.enable2DGS;
		}
		if ("falloff" in this.sparkRenderer && defaults.falloff !== undefined) {
			this.sparkRenderer.falloff = defaults.falloff;
		}
		if ("minPixelRadius" in this.sparkRenderer && defaults.minPixelRadius !== undefined) {
			this.sparkRenderer.minPixelRadius = defaults.minPixelRadius;
		}
		this._log("renderer fallback restore", {
			enable2DGS: this.sparkRenderer.enable2DGS,
			falloff: this.sparkRenderer.falloff,
			minPixelRadius: this.sparkRenderer.minPixelRadius
		});
	}

	_setSparkStyleUniforms(mode) {
		const uniforms = this.sparkShaderUniforms;
		if (!uniforms) {
			return;
		}

		if (uniforms.styleMode) {
			uniforms.styleMode.value = (mode === SPARK_STYLE_MODES.RING) ? 2 : (mode === SPARK_STYLE_MODES.POINT ? 1 : 0);
		}
		if (uniforms.styleRingSize) {
			uniforms.styleRingSize.value = (mode === SPARK_STYLE_MODES.RING) ? this.ringSize : 0.0;
		}
		if (uniforms.stylePointSize) {
			uniforms.stylePointSize.value = this.pointModeSize;
		}
	}

	_installSparkStyleShaderPatch() {
		const material = this.sparkRenderer?.material;
		if (!material || this.sparkShaderPatched) {
			return;
		}
		this.sparkShaderPatched = true;

		const originalOnBeforeCompile = material.onBeforeCompile;
		material.onBeforeCompile = (shader, renderer) => {
			if (typeof originalOnBeforeCompile === "function") {
				originalOnBeforeCompile(shader, renderer);
			}

			applySparkStyleShaderPatch(shader, this.pointModeSize);

			this.sparkShaderUniforms = shader.uniforms;
			this._setSparkStyleUniforms(this.mode);
		};

		material.customProgramCacheKey = () => SPARK_STYLE_PROGRAM_CACHE_KEY;
		material.needsUpdate = true;
	}

	_log() {
		// Intentionally muted: remove SparkStyle runtime logs.
	}
}

export {SPARK_STYLE_MODES};
